/**
 * Yard check-in candidate rules.
 *
 * AOP-VLTK-00144: a multi-leg order's second leg — never confirmed, never
 * dispatched, pickup window not yet open — was offered at its destination
 * yard minutes after leg 1 finished 600 miles away. Checking it in completed
 * the leg and rolled the whole order to Delivered.
 *
 * These helpers are pure so the rules can be tested without a yard screen.
 * The database enforces the same "leg must have started" rule in
 * `yard_progress_leg_delivered`; this is the UI half that stops staff from
 * being offered the leg in the first place.
 */

/** Legs arriving within ±24h of now are considered "expected at this yard". */
export const CHECK_IN_WINDOW_MS = 24 * 60 * 60 * 1000;

export interface CheckInCandidate {
  /** Parent order id. */
  id: string;
  unique_id?: string | null;
  legId: string | null;
  legSequence?: number | null;
  legCount: number;
  driverStatus?: string | null;
  /** Leg pickup time — a leg whose window has not opened has not started. */
  readyTime?: string | null;
  requested_delivery_time?: string | null;
  /** Sibling legs' statuses, including this leg's current one. */
  siblingStatuses?: (string | null)[];
}

/**
 * Has this leg actually started? Single-leg orders keep the legacy behaviour:
 * their status often lives only on the parent order, so a null leg status is
 * normal and must not block the gate.
 */
export function isLegCheckInEligible(
  candidate: Pick<CheckInCandidate, "legCount" | "driverStatus" | "readyTime">,
  now: Date = new Date(),
): boolean {
  if ((candidate.legCount ?? 1) <= 1) return true;
  const status = candidate.driverStatus ?? "";
  if (status === "" || status === "booked") return false;
  if (candidate.readyTime) {
    const ready = new Date(candidate.readyTime).getTime();
    if (Number.isFinite(ready) && ready > now.getTime()) return false;
  }
  return true;
}

/** Is the leg arriving inside the ±24h yard window? */
export function isWithinArrivalWindow(
  requestedDeliveryTime: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!requestedDeliveryTime) return false;
  const t = new Date(requestedDeliveryTime).getTime();
  if (!Number.isFinite(t)) return false;
  return Math.abs(t - now.getTime()) <= CHECK_IN_WINDOW_MS;
}

/** Apply every candidate rule to the raw list coming back from the query. */
export function filterCheckInCandidates<T extends CheckInCandidate>(
  rows: T[],
  now: Date = new Date(),
): T[] {
  return rows.filter(
    (r) => isWithinArrivalWindow(r.requested_delivery_time, now) && isLegCheckInEligible(r, now),
  );
}

/**
 * How many legs stay open once this one is checked in. A leg with no status
 * of its own counts as open.
 */
export function openLegsAfterCheckIn(candidate: CheckInCandidate): number {
  const statuses = candidate.siblingStatuses ?? [];
  if (!statuses.length) return 0;
  // This leg is about to close, so it never counts; the caller passes all
  // sibling statuses including this leg's, hence the -1 on a match.
  const open = statuses.filter((s) => s !== "delivered" && s !== "canceled").length;
  const thisLegOpen =
    candidate.driverStatus !== "delivered" && candidate.driverStatus !== "canceled";
  return Math.max(0, open - (thisLegOpen ? 1 : 0));
}

/**
 * Warning shown before a yard check-in that completes the whole order, so
 * staff know the action closes more than the leg in front of them.
 */
export function describeCheckInImpact(candidate: CheckInCandidate): string | null {
  if ((candidate.legCount ?? 1) <= 1) return null;
  if (openLegsAfterCheckIn(candidate) > 0) return null;
  const order = candidate.unique_id ? ` ${candidate.unique_id}` : "";
  return `This is the final open leg — checking in will mark order${order} delivered.`;
}

/**
 * Caution when the trailer's most recent yard event says it is standing at a
 * different site. In AOP-VLTK-00144 the trailer had been checked in at
 * El Paso three minutes earlier.
 */
export function describeLocationConflict(
  lastEvent: { event_type?: string | null; location_id?: string | null; city?: string | null } | null | undefined,
  currentLocationId: string,
): string | null {
  if (!lastEvent) return null;
  if (lastEvent.event_type !== "checked_in") return null;
  if (!lastEvent.location_id || lastEvent.location_id === currentLocationId) return null;
  const where = lastEvent.city ? ` at ${lastEvent.city}` : "";
  return `This trailer was last checked in${where}, not here. Confirm it is physically on this site before continuing.`;
}
